import React from 'react';
import { X, Zap, Activity, Heart, Building2, Pill, MessageCircle } from 'lucide-react';

const SIGN_CATEGORIES = [
  {
    category: '🆘 Emergência',
    signs: [
      { sign: 'Socorro', meaning: 'Socorro! Ajuda urgente' },
      { sign: 'Dor', meaning: 'Estou com dor' },
      { sign: 'Infarto', meaning: 'Tive infarto / dor forte no peito' },
      { sign: 'Derrame', meaning: 'Tive derrame cerebral' },
      { sign: 'Ambulância', meaning: 'Preciso de ambulância' },
      { sign: 'Urgência', meaning: 'É urgente' },
    ],
  },
  {
    category: '🤒 Sintomas',
    signs: [
      { sign: 'Febre', meaning: 'Estou com febre' },
      { sign: 'Dor', meaning: 'Estou com dor' },
      { sign: 'Tosse', meaning: 'Estou com tosse' },
      { sign: 'Tontura', meaning: 'Estou com tontura' },
      { sign: 'Enjoo', meaning: 'Estou enjoado / náusea' },
      { sign: 'Vomitar', meaning: 'Estou vomitando' },
      { sign: 'Sangramento', meaning: 'Estou sangrando' },
      { sign: 'Ansiedade', meaning: 'Estou ansioso' },
      { sign: 'Cansado', meaning: 'Estou cansado / sem energia' },
    ],
  },
  {
    category: '🫀 Órgãos / Local da dor',
    signs: [
      { sign: 'Cabeça', meaning: 'Dor de cabeça' },
      { sign: 'Peito', meaning: 'Dor no peito' },
      { sign: 'Barriga', meaning: 'Dor na barriga' },
      { sign: 'Costas', meaning: 'Dor nas costas' },
      { sign: 'Pulmão', meaning: 'Problema no pulmão' },
      { sign: 'Coração', meaning: 'Dor no coração' },
      { sign: 'Fígado', meaning: 'Problema no fígado' },
      { sign: 'Rim', meaning: 'Dor no rim' },
      { sign: 'Nariz', meaning: 'Nariz entupido / sangrando' },
      { sign: 'Ouvido', meaning: 'Dor no ouvido' },
      { sign: 'Garganta', meaning: 'Dor na garganta' },
    ],
  },
  {
    category: '🏥 Atendimento',
    signs: [
      { sign: 'Médico', meaning: 'Preciso falar com o médico' },
      { sign: 'Enfermeiro', meaning: 'Preciso de um enfermeiro' },
      { sign: 'Hospital', meaning: 'Preciso ir ao hospital' },
      { sign: 'Remédio', meaning: 'Preciso de remédio' },
      { sign: 'Exame', meaning: 'Preciso fazer exame' },
      { sign: 'Receita', meaning: 'Preciso de receita médica' },
      { sign: 'Internação', meaning: 'Preciso ser internado' },
      { sign: 'Cirurgia', meaning: 'Preciso de cirurgia' },
      { sign: 'Consulta', meaning: 'Quero agendar consulta' },
    ],
  },
  {
    category: '💊 Doenças Crônicas',
    signs: [
      { sign: 'Diabetes', meaning: 'Sou diabético' },
      { sign: 'Hipertensão', meaning: 'Tenho pressão alta' },
      { sign: 'Câncer', meaning: 'Tenho câncer' },
      { sign: 'Asma', meaning: 'Tenho asma' },
      { sign: 'Epilepsia', meaning: 'Tenho epilepsia' },
      { sign: 'Depressão', meaning: 'Estou com depressão' },
    ],
  },
  {
    category: '🗣️ Comunicação',
    signs: [
      { sign: 'Sim', meaning: 'Sim' },
      { sign: 'Não', meaning: 'Não' },
      { sign: 'Ajuda', meaning: 'Preciso de ajuda' },
      { sign: 'Obrigado', meaning: 'Obrigado' },
      { sign: 'Por_favor', meaning: 'Por favor' },
      { sign: 'Aqui', meaning: 'A dor é aqui' },
      { sign: 'Forte', meaning: 'A dor é forte' },
      { sign: 'Pior', meaning: 'Estou pior' },
      { sign: 'Melhor', meaning: 'Estou melhor' },
      { sign: 'Urgente', meaning: 'É urgente!' },
    ],
  },
];

const CATEGORY_META: Record<string, { icon: React.ReactNode; color: string; bg: string }> = {
  '🆘 Emergência':      { icon: <Zap className="w-3.5 h-3.5" />,          color: '#f87171', bg: 'rgba(239,68,68,0.1)' },
  '🤒 Sintomas':        { icon: <Activity className="w-3.5 h-3.5" />,      color: '#fb923c', bg: 'rgba(249,115,22,0.1)' },
  '🫀 Órgãos / Local da dor': { icon: <Heart className="w-3.5 h-3.5" />, color: '#f472b6', bg: 'rgba(244,114,182,0.1)' },
  '🏥 Atendimento':     { icon: <Building2 className="w-3.5 h-3.5" />,     color: '#38bdf8', bg: 'rgba(56,189,248,0.1)' },
  '💊 Doenças Crônicas':{ icon: <Pill className="w-3.5 h-3.5" />,          color: '#a78bfa', bg: 'rgba(167,139,250,0.1)' },
  '🗣️ Comunicação':     { icon: <MessageCircle className="w-3.5 h-3.5" />, color: '#2dd4bf', bg: 'rgba(45,212,191,0.1)' },
};

interface SignGuideProps {
  onClose?: () => void;
}

export const SignGuide: React.FC<SignGuideProps> = ({ onClose }) => {
  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div
        className="flex items-center justify-between px-5 py-4 flex-shrink-0"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
      >
        <div>
          <h3 className="text-sm font-bold text-white tracking-wide">GUIA DE SINAIS</h3>
          <p className="text-xs text-slate-500 mt-0.5">163 sinais · mostre para a câmera</p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-xl flex items-center justify-center transition-all"
            style={{ background: 'rgba(255,255,255,0.06)', color: '#94a3b8' }}
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Categories */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
        {SIGN_CATEGORIES.map(({ category, signs }) => {
          const meta = CATEGORY_META[category] ?? { icon: null, color: '#94a3b8', bg: 'rgba(148,163,184,0.1)' };
          return (
            <div key={category}>
              {/* Category header */}
              <div className="flex items-center gap-2 mb-2.5">
                <div
                  className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0"
                  style={{ background: meta.bg, color: meta.color }}
                >
                  {meta.icon}
                </div>
                <span className="text-xs font-bold tracking-widest uppercase" style={{ color: meta.color }}>
                  {category.replace(/^[^ ]+ /, '')}
                </span>
              </div>
              {/* Sign grid */}
              <div className="grid grid-cols-2 gap-1.5">
                {signs.map(({ sign, meaning }) => (
                  <div
                    key={sign}
                    className="rounded-xl px-3 py-2 min-w-0 transition-all"
                    style={{
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.06)',
                    }}
                  >
                    <div className="text-white font-semibold text-sm leading-tight">{sign}</div>
                    <div className="text-slate-500 text-[11px] leading-tight mt-0.5 truncate" title={meaning}>
                      {meaning}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
